/**
 * directorySearch — PROD-1115 (Alex 2026-10-17): the "Other doctors near you" list and the
 * "no Orella doctor nearby" banner (components/share/DirectoryNearby.jsx) ask the directory
 * through this one function.
 *
 * The API (GET /providers/search) answers a normal search in one of three ways:
 *  - 200 with `exact: true`: the offices search answered. This is the final list.
 *  - 200 with `exact: false` (`source: 'fallback'`): the fast fallback answered (each doctor at the
 *    main registry address). The list shows these rows at once, and ONE background request with
 *    `exact=1` asks for the offices search alone. A 200 `exact: true` answer replaces the rows
 *    silently (0 rows is a real empty answer). Any failure of that request keeps the fallback rows
 *    and shows no error.
 *  - a non-200 (503 directory_timeout, 502 directory_error, ...) or no answer at all: the search
 *    FAILED. The caller shows "We couldn't search right now" with Try again, never "No other
 *    doctors found here" (that text means a real 200 with 0 rows).
 * An older API that sends no `exact` is read as `exact: true` (no background request).
 *
 * `isCurrent()` is asked before every state change: an answer that belongs to an older search
 * (the ZIP, distance or search box changed, or the screen closed) is dropped, so it can never
 * overwrite the rows of a newer search.
 *
 * @param {object} o
 * @param {(params: object, opts: { signal?: AbortSignal }) => Promise<object>} o.search  providersAPI.search
 * @param {object} o.params   the query of the normal search
 * @param {AbortSignal} [o.signal]  aborted when the search is replaced or the screen closes
 * @param {() => boolean} o.isCurrent  false once a newer search started or the screen closed
 * @param {(answer: object) => Array} o.toRows  the rows the list shows for one 200 answer
 * @param {(rows: Array, exact: boolean) => void} o.onRows  a 200 answer's rows (fallback, then exact)
 * @param {() => void} o.onFailed  the normal search failed
 * @returns {Promise<void>} settles when the normal search has been handled (not the background one)
 */
export function directorySearch({ search, params, signal, isCurrent, toRows, onRows, onFailed }) {
  return search(params, { signal }).then(
    (r) => {
      if (!isCurrent()) return
      const exact = !(r && r.exact === false)
      onRows(toRows(r), exact)
      if (exact) return
      search({ ...params, exact: 1 }, { signal }).then(
        (x) => { if (isCurrent() && x && x.exact === true) onRows(toRows(x), true) },
        () => {}, // the fallback rows stay; no error is shown
      )
    },
    () => { if (isCurrent()) onFailed() },
  )
}
